import React, { useState } from "react";
import { Badge, Card, CardBody, CardImg, CardFooter, Modal, ModalBody } from "reactstrap";
import styles from "./styles.module.css";
import PlateFormat from "../../components/PlateFormat";

import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const TYPE_CONFIG = {
  hour: { bg: "#3b82f6", label: "xHora" },
  stay: { bg: "#22c55e", label: "xEstadia" },
  tn: { bg: "#ef4444", label: "xTurno" },
};
const DEFAULT_TYPE = { bg: "#6b7280", label: "--" };
const getTypeConfig = (type) => TYPE_CONFIG[type] || DEFAULT_TYPE;

function getPermanencia(initDate, endDate) {
  const start = moment(initDate);
  const end = endDate ? moment(endDate) : moment();
  const diff = moment.duration(end.diff(start));
  const days = Math.floor(diff.asDays());
  const hours = diff.hours();
  const minutes = diff.minutes();

  if (days >= 1) {
    return `${days}d ${hours}:${minutes.toString().padStart(2, "0")} hs.`;
  }
  return `${hours}:${minutes.toString().padStart(2, "0")} hs.`;
}

const getStatusText = (item) => {
  if (item.canceled === 1) return "Cancelado";
  if (item.closed === 1) return "Cerrado";
  return "Abierto";
};

const getStatusClass = (item) => {
  if (item.canceled === 1) return styles["status-canceled"];
  if (item.closed === 0) return styles["status-open"];
  return styles["status-closed"];
};

const ItemAnpr = ({ ticket: item, country }) => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const cfg = getTypeConfig(item.type);
  const plate = PlateFormat(item.plate, country);
  const imgSrc = item.picture ? `data:image/png;base64,${item.picture}` : null;

  return (
    <>
      <Card
        className="shadow-sm h-100"
        style={{ borderLeft: `4px solid ${cfg.bg}`, borderRadius: 8, overflow: "hidden", cursor: "pointer" }}
        onClick={() => setSelectedTicket(item)}
      >
        {/* Imagen + placa overlay + status */}
        <div className={styles["image-container"]}>
          {imgSrc && (
            <CardImg
              top
              src={imgSrc}
              alt={item.plate}
              className={styles["card-img"]}
            />
          )}
          <div className={styles["plate-overlay"]}>{plate}</div>
          <div className={`${styles["status-indicator"]} ${getStatusClass(item)}`}>
            {getStatusText(item)}
          </div>
        </div>

        {/* Body */}
        <CardBody className={styles["card-body"]}>
          <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
            <Badge color="dark" style={{ fontSize: 12 }}>{item.category_name}</Badge>
            <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>
              {cfg.label}
            </Badge>
            {item.is_reservation ? (
              <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>bookmark</span>Reserva
              </Badge>
            ) : null}
          </div>

          {item.car_detail && (
            <div className="text-muted text-center mt-1" style={{ fontSize: 12 }}>
              {item.car_detail}
            </div>
          )}

          {(item.place_name || item.floor_name) && (
            <div className="text-center mt-1" style={{ fontSize: 12, color: "#6c757d" }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>location_on</span>
              {item.place_name}{item.floor_name ? ` - ${item.floor_name}` : ""}
            </div>
          )}

          {item.obs && (
            <div className="text-center mt-1">
              <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 13 }}>sticky_note_2</span>{item.obs}
              </Badge>
            </div>
          )}
        </CardBody>

        {/* Footer */}
        <CardFooter className={styles["card-footer"]}>
          <div>
            <div>
              {moment(item.init_date).format("DD/MM HH:mm")}
              {item.end_date && <span> → {moment(item.end_date).format("DD/MM HH:mm")}</span>}
              {" — "}<b>{getPermanencia(item.init_date, item.end_date)}</b>
            </div>
            <div style={{ fontSize: "0.65rem" }}>{item.fullName}</div>
            {item.closed === 1 && item.closed_by_fullName && (
              <div style={{ fontSize: "0.65rem" }}>Cerrado por {item.closed_by_fullName}</div>
            )}
          </div>
          {item.canceled_details && (
            <Badge color="danger" className="mt-1" style={{ fontSize: 11, fontWeight: 500 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 12 }}>warning</span>{item.canceled_details}
            </Badge>
          )}
        </CardFooter>
      </Card>

      {/* Modal detalle */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        toggle={() => setSelectedTicket(null)}
        country={country}
      />
    </>
  );
};

const TicketDetailModal = ({ ticket, isOpen, toggle, country }) => {
  if (!ticket) return null;
  const cfg = getTypeConfig(ticket.type);
  const plate = PlateFormat(ticket.plate, country);
  const imgSrc = ticket.picture ? `data:image/png;base64,${ticket.picture}` : null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalBody className="p-0">
        {/* Imagen */}
        {imgSrc && (
          <img
            src={imgSrc}
            alt={ticket.plate}
            style={{ width: "100%", maxHeight: "50vh", objectFit: "cover" }}
          />
        )}

        {/* Datos */}
        <div className="p-3">
          {/* Placa + badges */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1 }}>{plate}</span>
            <div className="d-flex gap-1 flex-wrap">
              <Badge color="dark" style={{ fontSize: 12 }}>{ticket.category_name}</Badge>
              <Badge style={{ fontSize: 12, backgroundColor: cfg.bg, color: "#fff" }}>{cfg.label}</Badge>
              {ticket.canceled === 1 ? (
                <Badge color="danger" style={{ fontSize: 11 }}>Cancelado</Badge>
              ) : ticket.closed === 1 ? (
                <Badge color="secondary" style={{ fontSize: 11 }}>Cerrado</Badge>
              ) : (
                <Badge color="success" style={{ fontSize: 11 }}>Abierto</Badge>
              )}
              {ticket.is_reservation ? (
                <Badge color="warning" className="text-dark" style={{ fontSize: 11 }}>Reserva</Badge>
              ) : null}
            </div>
          </div>

          {/* Detalle del vehiculo */}
          {ticket.car_detail && (
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>directions_car</span>{ticket.car_detail}
            </div>
          )}

          {/* Ubicacion */}
          {(ticket.place_name || ticket.floor_name) && (
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 16 }}>location_on</span>
              {ticket.place_name}{ticket.floor_name ? ` - ${ticket.floor_name}` : ""}
            </div>
          )}

          {/* Observaciones */}
          {ticket.obs && (
            <div className="mb-2">
              <Badge color="warning" className="text-dark" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>sticky_note_2</span>{ticket.obs}
              </Badge>
            </div>
          )}

          {/* Motivo cancelacion */}
          {ticket.canceled_details && (
            <div className="mb-2">
              <Badge color="danger" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>warning</span>Motivo: {ticket.canceled_details}
              </Badge>
            </div>
          )}

          <hr className="my-2" />

          {/* Tiempos */}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div style={{ fontSize: 13 }}>
                <span className="material-symbols-rounded me-1 text-muted" style={{ fontSize: 16 }}>schedule</span>
                {moment(ticket.init_date).format("DD/MM/YYYY HH:mm")}
                {ticket.end_date && <span> → {moment(ticket.end_date).format("DD/MM/YYYY HH:mm")}</span>}
                {" — "}<b>{getPermanencia(ticket.init_date, ticket.end_date)}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>person</span>{ticket.fullName}
              </div>
              {ticket.closed === 1 && ticket.closed_by_fullName && (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>person_check</span>Cerrado por {ticket.closed_by_fullName}
                </div>
              )}
            </div>
            {ticket.id && (
              <span className="text-muted" style={{ fontSize: 11 }}>#{ticket.id}</span>
            )}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ItemAnpr;
